// SETTINGS V2 — Služba card 2 (Otváracie hodiny / Business Hours) — SETTINGS_V2_SP8 §5.2.
// Presentational only: owns NO business state. The deadline / cutoff / per-day
// state lives in ServicePageV2 (seed / dirty / save all need it); this card is a
// pure renderer wired via value + setter-callback props. Time inputs ride
// SInputField type="time" (the sanctioned shared-wrapper path — §2), NEVER a
// native <input>. Conditional-render parity: the body renders only when the
// deadline is enabled; the Mon-Sun grid only when per-day times are on too.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { SCard, SInputField, SToggle } from "../atoms";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

export interface ServiceBusinessHoursCardV2Props {
  deadlineEnabled: boolean;
  deadlineTime: string;
  usePerDay: boolean;
  perDayTimes: Record<string, string>;
  detectedTimezone: string;
  onToggleDeadline: (next: boolean) => void;
  onChangeTime: (v: string) => void;
  onTogglePerDay: (next: boolean) => void;
  onChangeDayTime: (day: string, v: string) => void;
  isPhone: boolean;
  animationDelay: string;
}

const rowTitleStyle: React.CSSProperties = { fontSize: 13.5, fontWeight: 600, color: "var(--ink)" };
const rowDescStyle: React.CSSProperties = { fontSize: 12.5, color: "var(--muted)", marginTop: 2, lineHeight: 1.45 };

const ServiceBusinessHoursCardV2: React.FC<ServiceBusinessHoursCardV2Props> = ({
  deadlineEnabled,
  deadlineTime,
  usePerDay,
  perDayTimes,
  detectedTimezone,
  onToggleDeadline,
  onChangeTime,
  onTogglePerDay,
  onChangeDayTime,
  isPhone,
  animationDelay,
}) => {
  const { translate: t } = useTranslation("settings");

  return (
    <SCard
      title={t("settings:service.businessHoursTitle")}
      style={{ animation: `sv2-card-reveal 0.4s ease-out ${animationDelay} both` }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Enable deadline toggle — always visible */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={rowTitleStyle}>{t("settings:service.enableDeadline")}</div>
            <div style={rowDescStyle}>{t("settings:service.enableDeadlineDescription")}</div>
          </div>
          <SToggle on={deadlineEnabled} onToggle={onToggleDeadline} ariaLabel={t("settings:service.enableDeadline")} />
        </div>

        {deadlineEnabled && (
          <>
            {/* Cutoff + auto-detected timezone (display-only, disabled) */}
            <div
              style={{
                display: "flex",
                flexDirection: isPhone ? "column" : "row",
                alignItems: isPhone ? "stretch" : "flex-end",
                gap: 14,
              }}
            >
              <div style={{ width: isPhone ? "100%" : 130 }}>
                <SInputField
                  label={t("settings:service.acceptOrdersUntil")}
                  type="time"
                  mono
                  value={deadlineTime}
                  onChange={onChangeTime}
                />
              </div>
              <div style={{ width: isPhone ? "100%" : 240 }}>
                <SInputField
                  label={t("settings:service.timezone")}
                  mono
                  disabled
                  value={detectedTimezone}
                  onChange={() => {}}
                />
              </div>
            </div>

            {/* Per-day toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                paddingTop: 12,
                borderTop: "1px solid var(--border-soft)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={rowTitleStyle}>{t("settings:service.usePerDayTimes")}</div>
                <div style={rowDescStyle}>{t("settings:service.usePerDayTimesDescription")}</div>
              </div>
              <SToggle on={usePerDay} onToggle={onTogglePerDay} ariaLabel={t("settings:service.usePerDayTimes")} />
            </div>

            {/* Mon-Sun inputs — 7-col grid on desktop/tablet, stacked rows on phone */}
            {usePerDay &&
              (isPhone ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {DAYS.map((day) => (
                    <div
                      key={day}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
                    >
                      <div style={{ fontSize: 13.5, color: "var(--ink)" }}>{t(`settings:service.days.${day}`)}</div>
                      <div style={{ width: 130 }}>
                        <SInputField
                          label=""
                          type="time"
                          mono
                          value={perDayTimes[day] || ""}
                          onChange={(v) => onChangeDayTime(day, v)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
                  {DAYS.map((day) => (
                    <div key={day} style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 8.5,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          textAlign: "center",
                          marginBottom: 4,
                          color: perDayTimes[day] ? "var(--muted)" : "var(--faint)",
                        }}
                      >
                        {t(`settings:service.days.${day}`).slice(0, 2)}
                      </div>
                      <SInputField
                        label=""
                        type="time"
                        mono
                        value={perDayTimes[day] || ""}
                        onChange={(v) => onChangeDayTime(day, v)}
                      />
                    </div>
                  ))}
                </div>
              ))}

            {/* Footnote — weekend-empty = closed · timezone auto-detected */}
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                color: "var(--faint)",
                letterSpacing: "0.04em",
                lineHeight: 1.5,
              }}
            >
              {t("settings:service.footnote")}
            </div>
          </>
        )}
      </div>
    </SCard>
  );
};

export default ServiceBusinessHoursCardV2;
